import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Copy, Keyboard, IndianRupee, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SmartIngredientInput from './SmartIngredientInput';
import { toCanonical, normalizeUnitKey, ACCEPTED_UNITS_FOR_UI } from '../lib/units';
import { createPrep, updatePrep } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import logger from '../lib/logger';

/**
 * PrepEditorDialog — fast operator-grade prep entry/edit.
 *
 * Modes:
 *   mode='create' → POST /api/preps
 *   mode='edit'   → PUT  /api/preps/{prep_id}   (server-side RBAC enforcement)
 *
 * Live cost = Σ (qty × ingredient.effective_price) per recipe line, with units
 * canonicalized on the fly. Cross-class mismatches show as "—".
 *
 * Keyboard:
 *   - Enter on a name/qty input:  advance to the next field of that row, or to the next row
 *   - Tab / Shift+Tab:            native cell-to-cell
 *   - Cmd/Ctrl+Enter:             Save
 *   - Cmd/Ctrl+D on a row:        Duplicate that row
 *   - Cmd/Ctrl+Delete on a row:   Remove that row
 */
const blankLine = () => ({ ingredient_id: '', qty: '', base_unit: 'gm', _key: Math.random().toString(36).slice(2) });

const PrepEditorDialog = ({ open, onClose, mode = 'create', existingPrep = null, ingredients = [], onSaved }) => {
  const [name, setName] = useState('');
  const [yieldQty, setYieldQty] = useState('');
  const [yieldUnit, setYieldUnit] = useState('kg');
  const [recipe, setRecipe] = useState([blankLine()]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Hydrate when opened
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && existingPrep) {
      setName(existingPrep.prep_name || '');
      setYieldQty(String(existingPrep.expected_yield || ''));
      setYieldUnit(existingPrep.base_unit || 'gm');
      const lines = (existingPrep.recipe || []).map((l) => ({
        ingredient_id: l.ingredient_id,
        qty: String(l.qty || ''),
        base_unit: l.base_unit || 'gm',
        _key: Math.random().toString(36).slice(2),
      }));
      setRecipe(lines.length ? lines : [blankLine()]);
      setReason('');
    } else {
      setName(''); setYieldQty(''); setYieldUnit('kg');
      setRecipe([blankLine()]); setReason('');
    }
  }, [open, mode, existingPrep]);

  // Build per-line cost preview using ingredient.effective_price (returned by /ingredients/master?include_pricing=true)
  const ingredientById = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => m.set(i.ingredient_id, i));
    return m;
  }, [ingredients]);

  const lineCosts = recipe.map((line) => {
    if (!line.ingredient_id || !line.qty) return { cost: 0, missing: true, mismatch: false };
    const ing = ingredientById.get(line.ingredient_id);
    if (!ing) return { cost: 0, missing: true, mismatch: false };
    const price = Number(ing.effective_price || 0);
    const ingUnit = ing.base_uom || ing.ingredient_base_unit || '';
    if (!price) return { cost: 0, missing: true, mismatch: false };

    const lineCanon = toCanonical(Number(line.qty || 0), line.base_unit);
    const ingCanon = toCanonical(1, ingUnit);
    if (lineCanon.conversion_failed || ingCanon.conversion_failed) return { cost: 0, missing: true, mismatch: false };
    if (lineCanon.base_unit !== ingCanon.base_unit) return { cost: 0, missing: false, mismatch: true };
    // price is per ingredient base_uom unit (e.g. per kg, per ml). Canonicalize price too.
    const priceCanon = price / (ingCanon.factor || 1);
    return { cost: lineCanon.qty * priceCanon, missing: false, mismatch: false };
  });

  const totalCost = lineCosts.reduce((s, c) => s + (c.cost || 0), 0);
  const yieldCanon = toCanonical(Number(yieldQty || 0), yieldUnit);
  const costPerYieldUnit = (!yieldCanon.conversion_failed && yieldCanon.qty > 0) ? totalCost / yieldCanon.qty : 0;
  const missingCount = lineCosts.filter((c) => c.missing && recipe[lineCosts.indexOf(c)]?.ingredient_id).length;
  const mismatchCount = lineCosts.filter((c) => c.mismatch).length;
  const invalidUnits = recipe.filter((l) => l.base_unit && toCanonical(1, l.base_unit).conversion_failed).length;
  const yieldUnitInvalid = yieldUnit && toCanonical(1, yieldUnit).conversion_failed;

  // unresolved ingredient_id rows (text typed but never selected)
  const hasUnresolvedRows = recipe.some((l) => l.qty && !l.ingredient_id);

  // ── handlers ─────────────────────────────────────────────────
  const updateLine = (idx, patch) => setRecipe((r) => r.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setRecipe((r) => [...r, blankLine()]);
  const addFiveLines = () => setRecipe((r) => [...r, ...Array.from({ length: 5 }, () => blankLine())]);
  const dupLine = (idx) => setRecipe((r) => {
    const copy = [...r];
    copy.splice(idx + 1, 0, { ...r[idx], _key: Math.random().toString(36).slice(2) });
    return copy;
  });
  const removeLine = (idx) => setRecipe((r) => r.length === 1 ? r : r.filter((_, i) => i !== idx));

  // qty-input refs for "Enter advances" UX
  const qtyRefs = useRef([]);
  const focusNextQty = (idx) => {
    const next = qtyRefs.current[idx + 1];
    if (next) next.focus();
    else { addLine(); setTimeout(() => qtyRefs.current[idx + 1]?.focus(), 50); }
  };

  const onGlobalKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onGlobalKeyDown);
    return () => document.removeEventListener('keydown', onGlobalKeyDown);
  }, [open, name, yieldQty, yieldUnit, recipe, reason]); // eslint-disable-line

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Prep name is required'); return; }
    const yq = Number(yieldQty);
    if (!yq || yq <= 0) { toast.error('Expected yield must be greater than 0'); return; }
    if (yieldUnitInvalid) { toast.error(`Yield unit '${yieldUnit}' is not recognized`); return; }
    const valid = recipe.filter((l) => l.ingredient_id && Number(l.qty) > 0);
    if (valid.length === 0) { toast.error('At least one resolved recipe line is required'); return; }
    if (hasUnresolvedRows) { toast.error('One or more rows have unresolved ingredients. Please select from the master list.'); return; }
    if (invalidUnits > 0) { toast.error('One or more lines have unrecognized units'); return; }

    setSaving(true);
    try {
      const payload = {
        prep_name: name.trim(),
        expected_yield: yq,
        base_unit: normalizeUnitKey(yieldUnit),
        recipe: valid.map((l) => ({
          ingredient_id: l.ingredient_id,
          qty: Number(l.qty),
          base_unit: normalizeUnitKey(l.base_unit),
        })),
      };
      if (mode === 'edit' && existingPrep) {
        if (reason.trim()) payload.reason = reason.trim();
        await updatePrep(existingPrep.prep_id, payload);
        toast.success('Prep updated');
      } else {
        payload.outlet_id = existingPrep?.outlet_id || null;
        await createPrep(payload);
        toast.success('Prep created');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      logger.error('save prep failed', e);
      toast.error(e?.response?.data?.detail || 'Failed to save prep');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="prep-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            {mode === 'edit' ? 'Edit Prep' : 'Create New Prep'}
            <span className="ml-auto text-xs font-normal text-zinc-500 flex items-center gap-1">
              <Keyboard className="w-3 h-3" /> Cmd/Ctrl+Enter to save · Cmd/Ctrl+D to duplicate row
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Header: name + yield */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-6">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Prep Name *</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Tomato Sauce" autoFocus={mode === 'create'}
                data-testid="editor-prep-name"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Expected Yield *</label>
              <input
                type="number" step="0.01" min="0.01" value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)} placeholder="5"
                data-testid="editor-yield-qty"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Yield Unit *</label>
              <select
                value={yieldUnit} onChange={(e) => setYieldUnit(e.target.value)}
                data-testid="editor-yield-unit"
                className={`w-full px-3 py-2 bg-[#09090b] border rounded-md text-sm outline-none ${yieldUnitInvalid ? 'border-red-500/60' : 'border-[#3f3f46] focus:border-emerald-500'}`}
              >
                {ACCEPTED_UNITS_FOR_UI.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              {yieldUnit !== normalizeUnitKey(yieldUnit) || (toCanonical(1, yieldUnit).base_unit !== yieldUnit) ? (
                <p className="text-xs text-zinc-500 mt-1">→ stored as <span className="font-mono">{toCanonical(1, yieldUnit).base_unit}</span></p>
              ) : null}
            </div>
          </div>

          {/* Recipe table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Recipe Lines *</label>
              <div className="flex gap-2">
                <button type="button" onClick={addLine} data-testid="editor-add-row" className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46] flex items-center gap-1"><Plus className="w-3 h-3" /> Add row</button>
                <button type="button" onClick={addFiveLines} data-testid="editor-add-5-rows" className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46]">+5 rows</button>
              </div>
            </div>

            <div className="border border-[#27272a] rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-0 px-2 py-2 bg-[#0c0c0e] text-xs text-zinc-500 font-medium uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Ingredient</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-2 text-right">Cost</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              <div className="divide-y divide-[#27272a]">
                {recipe.map((line, idx) => {
                  const cost = lineCosts[idx];
                  return (
                    <div key={line._key} className="grid grid-cols-12 gap-2 px-2 py-2 items-center" data-testid={`editor-row-${idx}`}>
                      <div className="col-span-1 text-xs text-zinc-500 font-mono">{idx + 1}</div>
                      <div className="col-span-5">
                        <SmartIngredientInput
                          ingredients={ingredients}
                          value={line.ingredient_id}
                          onChange={(id) => updateLine(idx, { ingredient_id: id })}
                          testIdPrefix={`editor-ing-${idx}`}
                          onEnter={() => qtyRefs.current[idx]?.focus()}
                          autoFocus={false}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          ref={(el) => qtyRefs.current[idx] = el}
                          type="number" step="0.01" min="0" value={line.qty}
                          onChange={(e) => updateLine(idx, { qty: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); focusNextQty(idx); }
                            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); dupLine(idx); }
                            if ((e.metaKey || e.ctrlKey) && (e.key === 'Delete' || e.key === 'Backspace')) { e.preventDefault(); removeLine(idx); }
                          }}
                          data-testid={`editor-qty-${idx}`}
                          className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div className="col-span-1">
                        <select
                          value={line.base_unit}
                          onChange={(e) => updateLine(idx, { base_unit: e.target.value })}
                          data-testid={`editor-unit-${idx}`}
                          className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                        >
                          {ACCEPTED_UNITS_FOR_UI.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className={`col-span-2 text-right text-sm font-mono ${cost.mismatch ? 'text-red-400' : cost.missing ? 'text-zinc-600' : 'text-emerald-400'}`} data-testid={`editor-line-cost-${idx}`}>
                        {cost.mismatch ? '—' : (cost.cost ? formatCurrency(cost.cost) : '—')}
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <button type="button" onClick={() => dupLine(idx)} title="Duplicate" data-testid={`editor-dup-${idx}`} className="p-1 text-zinc-500 hover:text-zinc-200"><Copy className="w-3.5 h-3.5" /></button>
                        {recipe.length > 1 && (
                          <button type="button" onClick={() => removeLine(idx)} title="Remove" data-testid={`editor-remove-${idx}`} className="p-1 text-red-500 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live summary */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-emerald-500/5 border border-emerald-500/20 rounded">
                <p className="text-xs text-zinc-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Batch Cost</p>
                <p className="text-lg font-mono font-bold" data-testid="editor-batch-cost">{formatCurrency(totalCost)}</p>
              </div>
              <div className="p-2 bg-emerald-500/5 border border-emerald-500/20 rounded">
                <p className="text-xs text-zinc-500">Per {toCanonical(1, yieldUnit).base_unit || yieldUnit}</p>
                <p className="text-lg font-mono font-bold text-emerald-400" data-testid="editor-cost-per-unit">{formatCurrency(costPerYieldUnit)}</p>
              </div>
              <div className={`p-2 border rounded ${missingCount ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-[#09090b] border-[#27272a]'}`}>
                <p className="text-xs text-zinc-500">Missing costs</p>
                <p className={`text-lg font-mono font-bold ${missingCount ? 'text-yellow-400' : 'text-zinc-300'}`} data-testid="editor-missing-costs">{missingCount}</p>
              </div>
              <div className={`p-2 border rounded ${(invalidUnits + mismatchCount) ? 'bg-red-500/5 border-red-500/30' : 'bg-[#09090b] border-[#27272a]'}`}>
                <p className="text-xs text-zinc-500">Unit issues</p>
                <p className={`text-lg font-mono font-bold ${(invalidUnits + mismatchCount) ? 'text-red-400' : 'text-zinc-300'}`} data-testid="editor-unit-issues">{invalidUnits + mismatchCount}</p>
              </div>
            </div>

            {hasUnresolvedRows && (
              <p className="mt-2 text-xs text-red-400 flex items-center gap-1" data-testid="editor-unresolved-warning">
                <AlertCircle className="w-3 h-3" /> One or more rows have an unresolved ingredient. Please select from the master list before saving.
              </p>
            )}
          </div>

          {mode === 'edit' && (
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Reason for edit (optional, audited)</label>
              <input
                type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Recipe revision per chef"
                data-testid="editor-edit-reason"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => onClose?.()} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded-md transition-colors" data-testid="editor-cancel-btn">Cancel</button>
            <button
              type="button" onClick={handleSave} disabled={saving}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50"
              data-testid="editor-save-btn"
            >{saving ? 'Saving…' : (mode === 'edit' ? 'Save Changes' : 'Create Prep')}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrepEditorDialog;
